import {
  DeleteOutlined,
  EditOutlined,
  FilterOutlined,
  MedicineBoxOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SyncOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Tag,
  Tooltip,
  message,
} from 'antd';
import PropTypes from 'prop-types';
import React, {useMemo, useState} from 'react';
import {FaUserTie} from 'react-icons/fa';
import {FaUserDoctor} from 'react-icons/fa6';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../../CommonComponents/Cards';
import {TableData} from '../../../../CommonComponents/TableData';
import {
  RqDeleteUser,
  RqUserLookupAll,
} from '../../../../ReactQuery/apis/rqAuthAPI';
import {selectAuth} from '../../../../Redux/selectors';

import EditEmployee from './EditEmployee/EditEmployee';
import EmployeeRegistration, {
  EMPLOYEE_ROLES,
} from './RegisterEmployee/RegisterEmployee';

const STATUS_OPTIONS = ['ACTIVE', 'INACTIVE'];

const ROLE_FILTER_OPTIONS = [
  {label: 'All Roles', value: 'ALL'},
  {label: 'Admin', value: 'ADMIN'},
  ...EMPLOYEE_ROLES.filter(role => role.value !== 'ADMIN'),
];

const EmployeeDetailModal = ({employee, onClose}) => (
  <Modal
    open={!!employee}
    onCancel={onClose}
    footer={null}
    title={(
      <span className={'text-primary-700 font-bold text-base'}>
        {employee?.fullName}
      </span>
    )}
    width={560}
    destroyOnHidden
  >
    {employee && (
      <Descriptions column={1} bordered size={'small'} className={'mb-2 mt-4'}>
        <Descriptions.Item label={'Role'}>
          <Tag color={employee.role === 'ADMIN' ? 'gold' : 'blue'}>
            {employee.role}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={'Email'}>
          {employee.email || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Mobile Number'}>
          {employee.mobileNumber || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Age'}>
          {employee.age || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Gender'}>
          {employee.gender || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Qualification'}>
          {employee.qualification || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Work Experience'}>
          {employee.workExperience || '—'}
        </Descriptions.Item>
        {employee.specialization && (
          <Descriptions.Item label={'Specialization'}>
            {employee.specialization}
          </Descriptions.Item>
        )}
        {employee.licenseNumber && (
          <Descriptions.Item label={'License Number'}>
            {employee.licenseNumber}
          </Descriptions.Item>
        )}
        {employee.availability && (
          <Descriptions.Item label={'Availability'}>
            {employee.availability}
          </Descriptions.Item>
        )}
        <Descriptions.Item label={'Address'}>
          {employee.address || '—'}
        </Descriptions.Item>
        <Descriptions.Item label={'Status'}>
          <Tag color={employee.status === 'ACTIVE' ? 'green' : 'red'}>
            {employee.status}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label={'Created On'}>
          {employee.createdOn
            ? new Date(employee.createdOn).toLocaleString()
            : '—'}
        </Descriptions.Item>
      </Descriptions>
    )}
  </Modal>
);

EmployeeDetailModal.propTypes = {
  employee: PropTypes.shape({
    id: PropTypes.string,
    fullName: PropTypes.string,
    role: PropTypes.string,
    email: PropTypes.string,
    mobileNumber: PropTypes.string,
    age: PropTypes.string,
    gender: PropTypes.string,
    qualification: PropTypes.string,
    workExperience: PropTypes.string,
    specialization: PropTypes.string,
    licenseNumber: PropTypes.string,
    availability: PropTypes.string,
    address: PropTypes.string,
    status: PropTypes.string,
    createdOn: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

const Employee = () => {
  const [registerModalOpen, setRegisterModalOpen] = useState(false);
  const [detailEmployee, setDetailEmployee] = useState(null);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [editEmployee, setEditEmployee] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState([]);

  const auth = useSelector(selectAuth);
  const token = auth?.tokenData;

  const usersQ = RqUserLookupAll(token, {
    enabled: !!token,
  });

  const allUsers = useMemo(
    () => (Array.isArray(usersQ.data) ? usersQ.data : []),
    [usersQ.data],
  );

  const employees = useMemo(() => {
    let list = [...allUsers].sort(
      (a, b) =>
        new Date(b.createdOn ?? 0) - new Date(a.createdOn ?? 0),
    );

    if (roleFilter !== 'ALL') {
      list = list.filter(user => user.role === roleFilter);
    }

    if (searchText.trim()) {
      const query = searchText.trim().toLowerCase();

      list = list.filter(
        user =>
          user.fullName?.toLowerCase().includes(query)
          || user.email?.toLowerCase().includes(query)
          || user.mobileNumber?.toLowerCase().includes(query),
      );
    }

    if (statusFilter.length > 0) {
      list = list.filter(user => statusFilter.includes(user.status));
    }

    return list;
  }, [allUsers, roleFilter, searchText, statusFilter]);

  const doctorCount = useMemo(
    () => allUsers.filter(user => user.role === 'DOCTOR').length,
    [allUsers],
  );

  const therapistCount = useMemo(
    () => allUsers.filter(user => user.role === 'THERAPIST').length,
    [allUsers],
  );

  const adminCount = useMemo(
    () => allUsers.filter(user => user.role === 'ADMIN').length,
    [allUsers],
  );

  const activeCount = useMemo(
    () => allUsers.filter(user => user.status === 'ACTIVE').length,
    [allUsers],
  );

  const deleteUser = RqDeleteUser({
    onSuccess: () => {
      message.success('User deleted successfully');
      usersQ.refetch();
    },
    onError: error =>
      message.error(
        error?.response?.data?.message
        || error?.response?.data?.error
        || error?.message
        || 'Failed to delete user',
      ),
    onSettled: () => setPendingDeleteId(null),
  });

  const handleDelete = id => {
    setPendingDeleteId(id);
    deleteUser.mutate({id, token});
  };

  const handleEditSuccess = () => {
    setEditEmployee(null);
    usersQ.refetch();
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter([]);
    setRoleFilter('ALL');
  };

  const handleRegisterSuccess = () => {
    setRegisterModalOpen(false);
    usersQ.refetch();
  };

  const hasActiveFilter =
    searchText.trim() !== ''
    || statusFilter.length > 0
    || roleFilter !== 'ALL';

  const columns = [
    {
      title: 'Sr. No.',
      key: 'srno',
      width: 70,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      sorter: (a, b) =>
        (a.fullName || '').localeCompare(b.fullName || ''),
      render: (name, record) => (
        <button
          type={'button'}
          onClick={() => setDetailEmployee(record)}
          className={'hover:text-primary-800 font-semibold text-base text-left hover:underline bg-transparent border-0 cursor-pointer p-0'}
        >
          {name}
        </button>
      ),
    },
    {
      title: 'Role',
      dataIndex: 'role',
      render: role => (
        <Tag color={role === 'ADMIN' ? 'gold' : 'blue'}>
          {role}
        </Tag>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
    },
    {
      title: 'Mobile Number',
      dataIndex: 'mobileNumber',
    },
    {
      title: 'Qualification',
      dataIndex: 'qualification',
      render: value =>
        value || <span className={'text-gray-400'}>—</span>,
    },
    {
      title: 'Experience',
      dataIndex: 'workExperience',
      render: value =>
        value || <span className={'text-gray-400'}>—</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: status => (
        <Tag
          color={status === 'ACTIVE' ? 'blue' : 'default'}
          className={'font-medium'}
        >
          {status}
        </Tag>
      ),
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      defaultSortOrder: 'descend',
      sorter: (a, b) =>
        new Date(a.createdOn ?? 0) - new Date(b.createdOn ?? 0),
      render: date =>
        date ? new Date(date).toLocaleDateString() : '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => {
        const isDeletingThisRow =
          pendingDeleteId === record.id && deleteUser.isPending;

        return (
          <Space
            size={4}
            onClick={event => event.stopPropagation()}
          >
            <Tooltip title={'Edit'}>
              <Button
                size={'small'}
                icon={<EditOutlined />}
                onClick={() => setEditEmployee(record)}
                disabled={pendingDeleteId === record.id}
              />
            </Tooltip>

            <Tooltip title={'Delete'}>
              <Popconfirm
                title={'Delete this user?'}
                description={'This action cannot be undone.'}
                onConfirm={() => handleDelete(record.id)}
                okButtonProps={{
                  danger: true,
                  loading: isDeletingThisRow,
                }}
                okText={'Delete'}
              >
                <Button
                  size={'small'}
                  icon={<DeleteOutlined />}
                  danger
                  loading={isDeletingThisRow}
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div className={'min-h-screen md:p-6 p-2'}>
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{
              background: 'linear-gradient(135deg, #b69c66, #b69c66)',
            }}
          >
            <FaUserDoctor style={{fontSize: 22}} />
          </div>

          <h1 className={'title !mb-0'}>Users</h1>
        </div>

        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={() => usersQ.refetch()}
            loading={usersQ.isFetching}
            className={'btn-secondary'}
          >
            Refresh
          </Button>

          <Button
            type={'primary'}
            icon={<PlusOutlined />}
            onClick={() => setRegisterModalOpen(true)}
            className={'btn-primary'}
          >
            Register Employee
          </Button>
        </Space>
      </div>

      <div className={'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6'}>
        <StatCard
          icon={<TeamOutlined />}
          label={'Total Users'}
          value={allUsers.length}
          colorClass={'bg-white'}
        />

        <StatCard
          icon={<FaUserTie />}
          label={'Total Admins'}
          value={adminCount}
          colorClass={'bg-white'}
        />

        <StatCard
          icon={<FaUserDoctor />}
          label={'Total Doctors'}
          value={doctorCount}
          colorClass={'bg-white'}
        />

        <StatCard
          icon={<MedicineBoxOutlined />}
          label={'Total Therapists'}
          value={therapistCount}
          colorClass={'bg-white'}
        />

        <StatCard
          icon={<TeamOutlined />}
          label={'Active Users'}
          value={activeCount}
          colorClass={'bg-primary-50'}
        />
      </div>

      <div className={'mb-5 p-4'}>
        <div className={'flex items-center gap-2 mb-3'}>
          <FilterOutlined className={'text-primary-500'} />

          <span className={'font-semibold text-gray-700 text-sm'}>
            Filters
          </span>

          {hasActiveFilter && (
            <Tag color={'blue'} className={'ml-1 text-xs'}>
              {employees.length} result
              {employees.length !== 1 ? 's' : ''}
            </Tag>
          )}
        </div>

        <div className={'flex flex-wrap gap-3 items-center'}>
          <Select
            placeholder={'Filter by role'}
            value={roleFilter}
            onChange={value => setRoleFilter(value)}
            style={{minWidth: 200}}
            options={ROLE_FILTER_OPTIONS}
          />

          <Input
            prefix={<SearchOutlined className={'text-gray-400'} />}
            placeholder={'Search by name, email or mobile…'}
            value={searchText}
            onChange={event => setSearchText(event.target.value)}
            allowClear
            style={{width: 280}}
          />

          <Select
            mode={'multiple'}
            placeholder={'Filter by status'}
            value={statusFilter}
            onChange={value => setStatusFilter(value)}
            style={{minWidth: 200}}
            options={STATUS_OPTIONS.map(status => ({
              value: status,
              label: (
                <Tag
                  color={status === 'ACTIVE' ? 'blue' : 'default'}
                  className={'text-xs my-0'}
                >
                  {status}
                </Tag>
              ),
            }))}
            allowClear
          />

          {hasActiveFilter && (
            <Button
              icon={<SyncOutlined />}
              onClick={handleClearFilters}
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      <Card
        className={'rounded-lg border border-primary-100 shadow-sm'}
        styles={{body: {padding: 20}}}
      >
        <TableData
          dataSource={employees}
          columns={columns}
          rowKey={record => record.id || record.email}
          loading={usersQ.isFetching}
          size={'middle'}
          scroll={{x: 1000}}
          rowClassName={'hover:bg-primary-50 transition-colors'}
        />
      </Card>

      <EmployeeRegistration
        open={registerModalOpen}
        onClose={() => setRegisterModalOpen(false)}
        onSuccess={handleRegisterSuccess}
      />

      <EditEmployee
        open={!!editEmployee}
        onClose={() => setEditEmployee(null)}
        onSuccess={handleEditSuccess}
        user={editEmployee}
        token={token}
      />

      <EmployeeDetailModal
        employee={detailEmployee}
        onClose={() => setDetailEmployee(null)}
      />
    </div>
  );
};

export default Employee;
